import apiClient from './apiClient';
import type {
    ApiResponse,
    PaginatedResponse,
    Appointment,
    CreateAppointmentRequest,
} from '../types/api';

const normalizeAppointment = (a: any): Appointment => ({
    ...a,
    appointment_date: a.appointment_date || a.scheduled_date,
    appointment_time: a.appointment_time || a.scheduled_time,
    status_code: a.status_code || a.status?.code,
    status_name: a.status_name || a.status?.name,
    type_name: a.type_name || a.type?.name,
});

export const appointmentService = {
    async getAll(params: { perPage?: number; page?: number; search?: string; doctor_id?: number; patient_id?: number; date?: string } = {}): Promise<PaginatedResponse<any>> {
        const queryParams = new URLSearchParams();
        if (params.perPage) queryParams.append('per_page', params.perPage.toString());
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.doctor_id) queryParams.append('doctor_id', params.doctor_id.toString());
        if (params.patient_id) queryParams.append('patient_id', params.patient_id.toString());
        if (params.date) queryParams.append('date', params.date);

        const response = await apiClient.get<PaginatedResponse<any>>(`/appointments?${queryParams.toString()}`);
        const raw = response.data;
        return { ...raw, data: (raw.data || []).map(normalizeAppointment) };
    },

    /**
     * Get today's appointments
     */
    async getToday(doctorId?: number): Promise<Appointment[]> {
        const params = doctorId ? `?doctor_id=${doctorId}` : '';
        const response = await apiClient.get<ApiResponse<Appointment[]>>(`/appointments/today${params}`);
        return response.data.data;
    },

    async getUpcoming(limit = 10, doctorId?: number): Promise<Appointment[]> {
        const params = new URLSearchParams({ limit: String(limit) });
        if (doctorId) params.append('doctor_id', String(doctorId));
        const response = await apiClient.get<ApiResponse<Appointment[]>>(`/appointments/upcoming?${params}`);
        return response.data.data;
    },

    /**
     * Get appointments by date
     */
    async getByDate(date: string): Promise<Appointment[]> {
        const response = await apiClient.get<ApiResponse<Appointment[]>>(`/appointments/by-date?date=${date}`);
        return response.data.data;
    },

    /**
     * Get appointment by ID
     */
    async getById(id: number): Promise<Appointment> {
        const response = await apiClient.get<ApiResponse<Appointment>>(`/appointments/${id}`);
        if (!response.data.data) throw new Error('Appointment not found');
        return normalizeAppointment(response.data.data);
    },

    /**
     * Create new appointment
     */
    async create(data: CreateAppointmentRequest): Promise<Appointment> {
        const response = await apiClient.post<ApiResponse<Appointment>>('/appointments', data);
        return response.data.data;
    },

    /**
     * Update appointment
     */
    async update(id: number, data: Partial<CreateAppointmentRequest>): Promise<void> {
        await apiClient.put(`/appointments/${id}`, data);
    },

    /**
     * Update appointment status
     */
    async updateStatus(id: number, statusId: number): Promise<void> {
        await apiClient.patch(`/appointments/${id}/status`, { status_id: statusId });
    },

    /**
     * Delete appointment
     */
    async delete(id: number): Promise<void> {
        await apiClient.delete(`/appointments/${id}`);
    },

    /**
     * Reception check-in: start a consultation from the appointment.
     */
    async startConsultation(id: number): Promise<{
        id: number;
        consultation_number: string;
        patient_id: number;
        doctor_id: number;
        appointment_id: number;
    }> {
        const response = await apiClient.post<ApiResponse<{
            id: number;
            consultation_number: string;
            patient_id: number;
            doctor_id: number;
            appointment_id: number;
        }>>(`/appointments/${id}/start-consultation`);
        return response.data.data;
    },
};

export default appointmentService;
